<template>
  <div class="brochure-display">
    <!-- Show the spread on wide displays -->
    <ClientOnly v-if="!isLoading">
      <BrochureSpreadDisplay
        v-if="!isSmallScreen"
        ref="brochureSpreadDisplay"
        :spreads="spreads"
        :hotspot-mode="hotspotMode"
        :brochure-title="brochureTitle"
      />
      <!-- Show a single page on small displays -->
      <BrochureSinglePageDisplay
        v-else
        ref="brochureSinglePage"
        :pages="pages"
        :hotspot-mode="hotspotMode"
        :brochure-title="brochureTitle"
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useRouteParams } from "@vueuse/router";
import {
  HotspotMode,
  type PageFragment,
  useCreateBrochureVisitStatisticMutation,
} from "~/graphql/generated";
import BrochureSpreadDisplay from "~/components/molecules/BrochureSpreadDisplay.vue";

const props = defineProps<{
  pages: PageFragment[];
  hotspotMode: HotspotMode;
  brochureTitle: string;
  isLoading: boolean;
}>();

const { width } = useWindowSize();
const isSmallScreen = computed(() => width.value < 1048);

// Split the pages array into chunks of 2 (pages per view), starting with a blank page
const spreads = computed(() => {
  const pages: (PageFragment | null)[] = [...props.pages];

  // Add a blank page to the start of the array because the first left page is always empty/ads
  pages.unshift(null);

  const spreads: Array<[PageFragment | null, PageFragment | null]> = [];

  // Loop over all pages and add them to the spreads array
  for (let i = 0; i < pages.length; i += 2) {
    spreads.push([pages[i], pages[i + 1]]);
  }

  return spreads;
});

// The handlers for the panzoom element and zoombar in the bottom
const brochureSpreadDisplay = ref<InstanceType<
  typeof BrochureSpreadDisplay
> | null>(null);
const brochureSinglePage = ref<ComponentPublicInstance | null>(null);

// The brochure visit statistic mutation
const { visitSource } = useVisitSource();
const brochureId = useRouteParams<string>("id");
const deviceId = useDeviceId();
const brochureVisitMutation = useCreateBrochureVisitStatisticMutation();

brochureVisitMutation.executeMutation({
  createBrochureVisitStatistic: {
    brochureId: brochureId.value,
    source: visitSource.value,
    deviceId: deviceId.value,
  },
});
</script>

<style lang="scss" scoped>
.brochure-display {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
}
</style>
